import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

interface Shot {
  id: string;
  player: string;
  speed: number;
  timestamp: Timestamp;
}

interface SpeedEntry {
  player: string;
  speed: number;
  frequency: number;
  lastHit: Date;
}

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  const [speedEntries, setSpeedEntries] = useState<SpeedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [globalStats, setGlobalStats] = useState({ 
    totalShots: 0, 
    mostFrequentSpeed: { speed: 0, frequency: 0 } 
  });

  const fetchShots = async () => {
    try {
      console.log('Starting to fetch shots...');
      setLoading(true);
      setError('');
      
      const shotsRef = collection(db, 'shots');
      console.log('Created shots collection reference');
      
      const q = query(shotsRef, orderBy('timestamp', 'desc'));
      console.log('Created query');
      
      const querySnapshot = await getDocs(q);
      console.log('Got query snapshot, size:', querySnapshot.size);
      
      const shots = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Shot data:', data);
        return {
          id: doc.id,
          ...data
        };
      }) as Shot[];
      
      console.log('Processed shots:', shots.length);

      // Group shots by player and speed
      const speedMap = new Map<string, SpeedEntry>();
      
      shots.forEach(shot => {
        if (!shot.player || !shot.speed) {
          console.log('Invalid shot data:', shot);
          return;
        }
        
        const key = `${shot.player}-${shot.speed}`;
        const existing = speedMap.get(key);
        
        const hitDate = shot.timestamp?.toDate() || new Date();
        
        if (existing) {
          existing.frequency += 1;
          if (hitDate > existing.lastHit) {
            existing.lastHit = hitDate;
          }
        } else {
          speedMap.set(key, {
            player: shot.player,
            speed: shot.speed,
            frequency: 1,
            lastHit: hitDate
          });
        }
      });

      console.log('Created speed map, entries:', speedMap.size);

      // Convert to array and sort by frequency and speed
      const entries = Array.from(speedMap.values()).sort((a, b) => {
        if (b.frequency !== a.frequency) {
          return b.frequency - a.frequency;
        }
        return b.speed - a.speed;
      });

      console.log('Sorted entries:', entries.length);

      // Calculate global stats
      const totalShots = shots.length;
      
      // Find most frequent speed
      const speedFrequencyMap = new Map<number, number>();
      shots.forEach(shot => {
        if (!shot.speed) return;
        const current = speedFrequencyMap.get(shot.speed) || 0;
        speedFrequencyMap.set(shot.speed, current + 1);
      });

      let mostFrequentSpeed = { speed: 0, frequency: 0 };
      speedFrequencyMap.forEach((frequency, speed) => {
        if (frequency > mostFrequentSpeed.frequency) {
          mostFrequentSpeed = { speed, frequency };
        }
      });

      setGlobalStats({
        totalShots,
        mostFrequentSpeed
      });

      setSpeedEntries(entries);
      console.log('Successfully set all state');
    } catch (error: any) {
      console.error('Error fetching shots:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setError(`Error loading leaderboard: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShots();
  }, []);

  const handleReset = async () => {
    if (!isAdmin) {
      setError('Only administrators can reset the leaderboard.');
      return;
    }

    if (window.confirm('Are you sure you want to reset the leaderboard? This action cannot be undone.')) {
      setLoading(true);
      try {
        const shotsRef = collection(db, 'shots');
        const querySnapshot = await getDocs(shotsRef);
        
        const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(deletePromises);
        
        setSpeedEntries([]);
        setGlobalStats({ 
          totalShots: 0, 
          mostFrequentSpeed: { speed: 0, frequency: 0 } 
        });
        setError('Leaderboard has been reset.');
      } catch (error: any) {
        console.error('Error resetting leaderboard:', error);
        setError(`Error resetting leaderboard: ${error.message}`);
      } finally {
        setLoading(false);
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const handleViewAnalytics = () => {
    if (currentUser) {
      navigate('/analytics');
    } else {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto text-center py-8">
        <div className="text-gray-300">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-[#f47521]">Shot Speed Leaderboard</h2>
        {isAdmin && (
          <div className="space-x-4">
            <button
              onClick={() => navigate('/tracker')}
              className="bg-[#f47521] text-white hover:bg-[#ff8534] font-bold py-2 px-6 rounded transition-colors"
            >
              Back to Tracker
            </button>
            {speedEntries.length > 0 && (
              <button
                onClick={handleReset}
                className="border border-[#f47521] text-[#f47521] hover:bg-[#f47521] hover:text-white font-bold py-2 px-6 rounded transition-colors"
              >
                Reset Leaderboard
              </button>
            )}
          </div>
        )}
      </div>

      <div className="bg-[#1e2736] rounded p-6 mb-6">
        <h3 className="text-xl font-bold text-[#f47521] mb-4">Global Stats</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-[#151b27] rounded p-4 text-center">
            <p className="text-gray-400 text-sm mb-1">Total Shots Hit</p>
            <p className="text-4xl font-bold text-white">{globalStats.totalShots}</p>
          </div>
          <div className="bg-[#151b27] rounded p-4 text-center">
            <p className="text-gray-400 text-sm mb-1">Most Hit Speed</p>
            <p className="text-4xl font-bold text-white">
              {globalStats.mostFrequentSpeed.speed > 0 ? (
                <>{globalStats.mostFrequentSpeed.speed} KPH<br />
                <span className="text-sm text-gray-400">({globalStats.mostFrequentSpeed.frequency} times)</span></>
              ) : (
                '-'
              )}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-[#1e2736] border border-red-500 text-white px-6 py-3 rounded">
          {error}
        </div>
      )}
      
      <div className="bg-[#1e2736] rounded overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#151b27]">
              <th className="py-4 px-6 text-left text-sm font-semibold text-gray-400">Rank</th>
              <th className="py-4 px-6 text-left text-sm font-semibold text-gray-400">Player</th>
              <th className="py-4 px-6 text-left text-sm font-semibold text-gray-400">Shot Speed (KPH)</th>
              <th className="py-4 px-6 text-left text-sm font-semibold text-gray-400">Frequency</th>
              <th className="py-4 px-6 text-left text-sm font-semibold text-gray-400">Last Hit</th>
            </tr>
          </thead>
          <tbody>
            {speedEntries.map((entry, index) => (
              <tr
                key={`${entry.player}-${entry.speed}`}
                className={`border-b border-[#151b27] ${
                  index % 2 === 0 ? 'bg-[#1e2736]' : 'bg-[#151b27]'
                } hover:bg-[#2a3343] transition-colors`}
              >
                <td className="py-4 px-6 text-gray-300">{index + 1}</td>
                <td className="py-4 px-6 text-[#f47521] font-medium">{entry.player}</td>
                <td className="py-4 px-6 text-gray-300">{entry.speed}</td>
                <td className="py-4 px-6 text-[#3b82f6] font-bold">{entry.frequency}</td>
                <td className="py-4 px-6 text-gray-400">
                  {entry.lastHit?.toLocaleDateString() || 'N/A'}
                </td>
              </tr>
            ))}
            {speedEntries.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-400">
                  No shots recorded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!currentUser && speedEntries.length > 0 && (
        <div className="mt-8 text-center">
          <button
            onClick={handleViewAnalytics}
            className="bg-[#f47521] text-white hover:bg-[#ff8534] font-bold py-3 px-8 rounded transition-colors"
          >
            View Detailed Analytics
          </button>
          <p className="mt-2 text-gray-400">
            Login required to access detailed shot analytics and statistics
          </p>
        </div>
      )}
    </div>
  );
};

export default Leaderboard; 